import React, { useEffect, useState } from "react";
import { Alert, Button, Form, InputNumber, Select, Space, Typography } from "antd";
import { useParams } from "react-router-dom";
import { SectionPanelProps } from "./SectionTypes";
import { useClientConfigService } from "../../../services/clients/ClientConfigServiceProvider";

const { Text, Paragraph } = Typography;

const LAST_DAY = "last" as const;

export interface BillingSectionValue {
    // null means "last day of month"
    monthlyCycleDay: number | null;
    proRataCutoffDay: number;
}

export const DEFAULT_BILLING: BillingSectionValue = {
    monthlyCycleDay: null,
    proRataCutoffDay: 23,
};

// Fixed cycle days are 1..28 so the anchor exists in every month; "last day of
// month" is the catch-all for month-end (and the legacy default).
const CYCLE_DAY_OPTIONS = [
    { value: LAST_DAY, label: "Last day of month" },
    ...Array.from({ length: 28 }, (_, i) => {
        const day = i + 1;
        return { value: String(day), label: `Day ${day}` };
    }),
];

function isValid(value: BillingSectionValue): boolean {
    const cutoffOk =
        Number.isInteger(value.proRataCutoffDay) &&
        value.proRataCutoffDay >= 1 &&
        value.proRataCutoffDay <= 31;
    const cycleOk =
        value.monthlyCycleDay === null ||
        (Number.isInteger(value.monthlyCycleDay) &&
            value.monthlyCycleDay >= 1 &&
            value.monthlyCycleDay <= 28);
    return cutoffOk && cycleOk;
}

// Controlled billing-cycle section: the day a monthly membership runs to / a
// direct debit collects on, plus the pro-rata roll-forward cut-off.
export const BillingSection: React.FC<SectionPanelProps<BillingSectionValue>> = ({
    value,
    onChange,
    readOnly,
}) => (
    <Form layout="vertical" data-testid="settings-billing-form" disabled={readOnly}>
        <Form.Item
            label="Monthly cycle day"
            help="The day a monthly membership runs to and direct debits collect on."
        >
            <Select
                data-testid="settings-billing-cycle-day"
                style={{ width: "100%", maxWidth: 320 }}
                value={value.monthlyCycleDay === null ? LAST_DAY : String(value.monthlyCycleDay)}
                options={CYCLE_DAY_OPTIONS}
                onChange={(next) =>
                    onChange({
                        ...value,
                        monthlyCycleDay: next === LAST_DAY ? null : Number(next),
                    })
                }
            />
        </Form.Item>

        <Form.Item
            label="Pro-rata cut-off day"
            help="A member joining on or after this day pays an extra cycle (their first period rolls forward)."
        >
            <InputNumber
                data-testid="settings-billing-cutoff-day"
                inputMode="numeric"
                min={1}
                max={31}
                value={value.proRataCutoffDay}
                onChange={(next) =>
                    onChange({ ...value, proRataCutoffDay: typeof next === "number" ? next : value.proRataCutoffDay })
                }
            />
        </Form.Item>
    </Form>
);

// Adapter that mounts BillingSection inside the Settings shell: loads current
// billing config from /config, tracks dirty state, and saves via PUT.
export const BillingSectionPanel: React.FC<SectionPanelProps<unknown>> = () => {
    const service = useClientConfigService();
    const { clientId: clientIdParam } = useParams<{ clientId: string }>();
    const clientId = clientIdParam !== undefined ? Number(clientIdParam) : NaN;
    const [initial, setInitial] = useState<BillingSectionValue | null>(null);
    const [value, setValue] = useState<BillingSectionValue>(DEFAULT_BILLING);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!Number.isFinite(clientId)) {
            setLoadError("Invalid client id in URL.");
            return;
        }
        setLoadError(null);
        let cancelled = false;
        (async () => {
            try {
                const config = await service.getConfig(clientId);
                if (cancelled) return;
                const billing = config?.settings.billing;
                const loaded: BillingSectionValue = {
                    monthlyCycleDay: billing?.monthlyCycleDay ?? null,
                    proRataCutoffDay: billing?.proRataCutoffDay ?? DEFAULT_BILLING.proRataCutoffDay,
                };
                setInitial(loaded);
                setValue(loaded);
            } catch (err) {
                if (!cancelled) setLoadError(err instanceof Error ? err.message : "Failed to load settings.");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [service, clientId]);

    const dirty = initial !== null && JSON.stringify(initial) !== JSON.stringify(value);

    const handleSave = async () => {
        if (!Number.isFinite(clientId) || !isValid(value)) return;
        setSaving(true);
        setSaveError(null);
        try {
            await service.updateBilling(clientId, {
                monthlyCycleDay: value.monthlyCycleDay,
                proRataCutoffDay: value.proRataCutoffDay,
            });
            setInitial(value);
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : "Failed to save settings.");
        } finally {
            setSaving(false);
        }
    };

    if (loadError) {
        return <Alert type="error" message={loadError} data-testid="settings-billing-load-error" />;
    }
    if (initial === null) {
        return <Text type="secondary">Loading…</Text>;
    }

    return (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Controls when monthly memberships renew and when direct debits collect. Leave as
                <Text strong> Last day of month</Text> for the standard cycle.
            </Paragraph>
            <BillingSection value={value} onChange={setValue} />
            {saveError && <Alert type="error" message={saveError} data-testid="settings-billing-save-error" />}
            <Space>
                <Button
                    type="primary"
                    data-testid="settings-billing-save-btn"
                    onClick={handleSave}
                    loading={saving}
                    disabled={!dirty || !isValid(value)}
                >
                    Save changes
                </Button>
                <Button
                    data-testid="settings-billing-discard-btn"
                    onClick={() => initial && setValue(initial)}
                    disabled={!dirty || saving}
                >
                    Discard
                </Button>
            </Space>
        </Space>
    );
};
